import {
  AlertTriangle,
  Award,
  Bell,
  Building2,
  CalendarClock,
  CalendarDays,
  ClipboardCheck,
  ClipboardList,
  FileCheck,
  Heart,
  ListChecks,
  type LucideIcon,
  Megaphone,
  MessageSquare,
  PartyPopper,
  Reply,
  Settings,
  Sparkles,
  Target,
  TrendingUp,
  Users,
  Wallet,
} from "lucide-react";
import type { NotificationCategory } from "@/features/notifications/schemas/notification.schema";

/// Single source of truth for the notification categories' display
/// metadata. Used by the bell dropdown row icons, the inbox category
/// filter, and the preferences settings panel. Order matches spec §2.5
/// so the panel always renders the same row order regardless of which
/// rows are sparsely stored.
export interface NotificationCategoryToken {
  category: NotificationCategory;
  /// Human label for the preferences row + filter dropdown.
  label: string;
  /// One-line description shown under the label in the preferences UI.
  description: string;
  /// Icon for inbox rows + the preferences row.
  icon: LucideIcon;
  /// `true` for categories whose toggle is locked in the preferences UI
  /// (mirrors shore's `ALWAYS_ON_CATEGORIES`).
  isAlwaysOn: boolean;
}

export const NOTIFICATION_CATEGORY_TOKENS: ReadonlyArray<NotificationCategoryToken> = [
  {
    category: "feedback",
    label: "Feedback",
    description: "Feedback sent to you, plus acknowledgements + promotions.",
    icon: MessageSquare,
    isAlwaysOn: false,
  },
  {
    category: "comments",
    label: "Comments",
    description: "Replies on threads where you're a named participant.",
    icon: Reply,
    isAlwaysOn: false,
  },
  {
    category: "mentions",
    label: "Mentions",
    description: "When someone @-mentions you in a comment.",
    icon: Bell,
    isAlwaysOn: false,
  },
  {
    category: "recognition",
    label: "Recognition",
    description: "Kudos you receive, plus claps on your kudos.",
    icon: Heart,
    isAlwaysOn: false,
  },
  {
    category: "team_recognition",
    label: "Team recognition",
    description: "Kudos given to teammates in your department.",
    icon: Award,
    isAlwaysOn: false,
  },
  {
    category: "one_on_one",
    label: "1:1s",
    description: "Scheduling, rescheduling, and cancellations on your 1:1s.",
    icon: Users,
    isAlwaysOn: false,
  },
  {
    category: "todos",
    label: "Todos",
    description: "Todos assigned to you, plus status changes.",
    icon: Sparkles,
    isAlwaysOn: false,
  },
  {
    category: "pulse_insights",
    label: "Pulse insights",
    description: "AI-generated briefs about you or your team.",
    icon: TrendingUp,
    isAlwaysOn: false,
  },
  {
    category: "pulse_surveys",
    label: "Surveys",
    description: "When you have a survey or pulse to answer.",
    icon: ClipboardList,
    isAlwaysOn: false,
  },
  {
    category: "pulse_actions",
    label: "Pulse actions",
    description: "Pulse actions assigned to you, plus measurement results.",
    icon: ListChecks,
    isAlwaysOn: false,
  },
  {
    category: "performance_reviews",
    label: "Performance reviews",
    description: "Review cycle steps, deadlines, and status updates.",
    icon: ClipboardCheck,
    isAlwaysOn: false,
  },
  {
    category: "goals",
    label: "Goals",
    description: "Goal updates, check-in prompts, and assignment changes.",
    icon: Target,
    isAlwaysOn: false,
  },
  {
    category: "workspace_admin",
    label: "Workspace admin",
    description: "New members joining, import completions, admin housekeeping.",
    icon: Settings,
    isAlwaysOn: false,
  },
  {
    category: "critical_safety_alerts",
    label: "Critical safety alerts",
    description: "Cannot be disabled. Used for safety reviews and critical pulse signals.",
    icon: AlertTriangle,
    isAlwaysOn: true,
  },
  {
    // Module 17, HRMS Leave. Icon matches the Leave sidebar group
    // (sidebar-nav.config.ts) so the same feature reads the same way in
    // both places.
    category: "leave",
    label: "Leave",
    description: "Leave requests filed for you to decide, and updates on your own requests.",
    icon: CalendarDays,
    isAlwaysOn: false,
  },
  // Module 00, HR foundation (FND-028). Labels are locked copy, do not
  // reword. None of these are always-on: an HR notification is never so
  // urgent that a member may not opt out of it.
  {
    category: "hr_requests",
    label: "Requests and approvals",
    description: "Requests filed for you to decide, and decisions on ones you filed.",
    icon: FileCheck,
    isAlwaysOn: false,
  },
  {
    category: "hr_scheduling",
    label: "HR scheduling",
    description: "Shift, roster, and schedule changes that affect you.",
    icon: CalendarClock,
    isAlwaysOn: false,
  },
  {
    category: "hr_payroll_self",
    label: "My pay",
    description: "Payslips, pay changes, and other payroll updates about you.",
    icon: Wallet,
    isAlwaysOn: false,
  },
  {
    category: "hr_updates",
    label: "HR updates",
    description: "General HR announcements and policy updates.",
    icon: Megaphone,
    isAlwaysOn: false,
  },
  {
    category: "hr_admin_ops",
    label: "HR admin operations",
    description: "Housekeeping for HR admins, imports, and workflow changes.",
    icon: Building2,
    isAlwaysOn: false,
  },
  {
    category: "hr_celebrations",
    label: "Celebrations",
    description: "Work anniversaries, birthdays, and other team milestones.",
    icon: PartyPopper,
    isAlwaysOn: false,
  },
  {
    category: "pulse_answer_replies",
    label: "Survey answer conversations",
    description:
      "When an admin replies to an answer you wrote, or when someone replies or shares their name in a conversation you replied to as an admin.",
    icon: Reply,
    isAlwaysOn: false,
  },
];
